import type { Absence, Appointment, ScheduleEntry, TaskListItem } from '@myapp/contract';
import { getAbsenceCategoryLabel } from '../../../entities/absence';
import { getAppointmentAttachmentTotal } from '../../../entities/appointment';
import { workOrderAddress } from '../../../shared/helpers/address';
import { summarizeTechnicianFeedback } from '../../../shared/lib/technicianFeedback';
import {
  formatTimeHHmm,
  resolveBlockTimes,
  type BoardSpan,
  type TravelKind,
} from '../../../shared/lib/schedule';
import { COMPLETED_TRAVEL_COLOR } from '../../../shared/lib/timelineColors';
import { getBlockColors } from './blockColors';
import type { BlockSegment } from './boardTypes';
import { showsReschedulingState } from '../../../shared/lib/schedule';

const MS_PER_MINUTE = 60_000;

type SegmentKind = BlockSegment['kind'];

/**
 * Splits a block into Anfahrt / Arbeit / Abfahrt and fills each with its text.
 *
 * Widths come from the **clamped** span so the segments always sum to the drawn width; the `HH:mm`
 * labels come from the entry's **true** times, so a block clipped at 06:00 still announces when it
 * really started. The three durations are `resolveBlockTimes`' `actual ?? planned` — the same rule
 * the server derives `startsAt`/`endsAt` with, which is why the strips and the box agree.
 *
 * The running marker sits on the strip of the phase that is actually open — a technician on the
 * Anfahrt rings the Anfahrt, not the work. When that strip has no width (no travel planned, or
 * clipped at the window edge) the work strip carries it instead: a ringless running block would
 * say the technician is idle.
 */
export function buildSegments(
  entry: ScheduleEntry,
  span: BoardSpan,
  appointment: Appointment | null,
  task: TaskListItem | null,
  absence: Absence | null,
  /** The clock the span was drawn with, so an overrunning phase's strip fills the stretched box. */
  nowMs?: number,
  /** From `detectTransfers` — a handover from/to the neighbouring block prints as Transfer. */
  kinds?: { travel: TravelKind; departure: TravelKind },
): BlockSegment[] {
  const total = span.endMin - span.startMin;
  const status = appointment?.status ?? task?.status ?? null;
  const { workColor, travelColor } = getBlockColors(entry.type, status);

  // Absences and breaks are a single solid bar — no travel, no order number.
  if (entry.type === 'ABSENCE' || entry.type === 'BREAK') {
    return [
      {
        kind: 'work',
        minutes: total,
        color: workColor,
        // A clipped block does not start where it is drawn: a full-day Krankmeldung really begins
        // at 00:00 and is drawn on the shift, so announcing `00:00` over a bar that starts at 07:00
        // reads as a bug. The category alone is the honest label; the tooltip still has the range.
        topLabel:
          span.clippedStart || span.clippedEnd ? undefined : formatTimeHHmm(entry.startsAt),
        label:
          entry.type === 'BREAK'
            ? entry.label ?? 'Pause'
            : getAbsenceCategoryLabel(absence?.category ?? entry.category),
        // Always `false` on a Pause — see `showsReschedulingState`. An Abwesenheit keeps the pill.
        needsRescheduling: showsReschedulingState(entry),
      },
    ];
  }

  const times = resolveBlockTimes(entry, nowMs, status);
  const trueTravel = times.travelMinutes;
  const trueWork = times.workMinutes;
  const trueDeparture = times.departureMinutes;

  const anfahrtStartMs = new Date(times.liveStartsAt).getTime();
  const workStartMs = anfahrtStartMs + trueTravel * MS_PER_MINUTE;
  const abfahrtStartMs = workStartMs + trueWork * MS_PER_MINUTE;

  // Clipped edges lose their travel strip rather than showing a partial one.
  const travelMinutes = span.clippedStart ? 0 : Math.min(trueTravel, total);
  const departureMinutes = span.clippedEnd ? 0 : Math.min(trueDeparture, total - travelMinutes);
  const workMinutes = Math.max(0, total - travelMinutes - departureMinutes);

  const ringKind = resolveRingKind(times.phase, { travel: travelMinutes, work: workMinutes, departure: departureMinutes });

  // The drive out is over once the work runs — drawn as done, the same as the worker calendar.
  const anfahrtColor = times.isInProgress ? COMPLETED_TRAVEL_COLOR : travelColor;

  const external = appointment?.workOrder?.external;

  // The subject line is an address, never `entry.label` — that is the server's `Termin 20266555`
  // string, which the old timeline never displayed (`label: location ?? undefined`).
  // `workOrderAddress` leads with `hauseingang` — street + house number of the actual entrance —
  // so a block shows where the technician drives to, not the Wirtschaftseinheit description.
  const workLabel =
    entry.type === 'APPOINTMENT'
      ? workOrderAddress(external) ?? undefined
      : task?.title ?? undefined;

  return [
    {
      kind: 'travel',
      minutes: travelMinutes,
      color: anfahrtColor,
      topLabel: formatTimeHHmm(new Date(anfahrtStartMs).toISOString()),
      label: kinds?.travel ?? 'Anfahrt',
      subLabel: `${trueTravel} min`,
      inProgress: ringKind === 'travel',
    },
    {
      kind: 'work',
      minutes: workMinutes,
      color: workColor,
      topLabel: formatTimeHHmm(new Date(workStartMs).toISOString()),
      label: workLabel,
      subLabel: entry.type === 'APPOINTMENT' ? external?.gewerkeText ?? undefined : undefined,
      badge:
        external != null ? `Nr. ${external.auftragJahr}/${external.auftragNr}` : undefined,
      attachmentCount: appointment ? getAppointmentAttachmentTotal(appointment) : 0,
      // Both owners carry the comment; the task row carries a count instead of the files.
      technicianFeedback: summarizeTechnicianFeedback(appointment ?? task ?? {}),
      inProgress: ringKind === 'work',
      // Only on the work strip: Anfahrt and Abfahrt are 15-minute slivers with no room for a pill,
      // and the flag belongs to the whole block anyway — the ghost fill carries it. Through the
      // predicate although this branch can never be a Pause: one rule, no exceptions.
      needsRescheduling: showsReschedulingState(entry),
    },
    {
      kind: 'departure',
      minutes: departureMinutes,
      color: travelColor,
      topLabel: formatTimeHHmm(new Date(abfahrtStartMs).toISOString()),
      label: kinds?.departure ?? 'Abfahrt',
      subLabel: `${trueDeparture} min`,
      inProgress: ringKind === 'departure',
    },
  ];
}

/** Which strip wears the running marker — the open phase's own, or the work strip as fallback. */
function resolveRingKind(
  phase: ReturnType<typeof resolveBlockTimes>['phase'],
  minutes: Record<SegmentKind, number>,
): SegmentKind | null {
  const activeKind: SegmentKind | null =
    phase === 'ANFAHRT'
      ? 'travel'
      : phase === 'ARBEIT'
        ? 'work'
        : phase === 'ABFAHRT'
          ? 'departure'
          : null;
  if (activeKind == null) return null;
  return minutes[activeKind] > 0 ? activeKind : 'work';
}
